/* Where the code secret lives on the device.
 *
 * THIS IS A SEAM, AND IT IS OPEN ON PURPOSE.
 * Day eight of the iOS storage probe is 13 September 2026, and its result is
 * the input to how this is finally implemented. Until then the rule from the
 * brief applies: build the loop with whatever is expedient, behind something
 * that can be changed.
 *
 * So everything the app does with the secret goes through `SecretStore`, and
 * the localStorage implementation below is explicitly provisional. Swapping it
 * for IndexedDB, for the Origin Private File System, or for "do not persist at
 * all, refetch on every launch" is one object, not a search across components.
 *
 * WHAT MAKES THAT CHEAP
 * `POST /v1/me/session` returns the secret every time. So local storage is a
 * cache the app may lose rather than a vault it must keep, and the worst case
 * for any storage decision is one extra round trip on a cold launch, not a
 * customer who can no longer produce a code. If the probe comes back saying
 * iOS evicts after eight days, the honest answer might be to store nothing.
 */
import type { CodeSecret } from "./totp";

export type StoredSecret = CodeSecret & { userId: string; skewMs?: number };

export interface SecretStore {
  read(): StoredSecret | null;
  write(value: StoredSecret): void;
  clear(): void;
  /** What this implementation actually is, for the diagnostics screen. The
   *  probe's answer is about a specific mechanism, so the app has to be able
   *  to say which one it used. */
  readonly kind: string;
}

const KEY = "damga.code.v1";

/** PROVISIONAL. See the module note. */
export function localStorageSecretStore(): SecretStore {
  return {
    kind: "localStorage",
    read() {
      try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredSecret;
        // A partially written or half-migrated value is treated as absent
        // rather than trusted, because the failure it causes downstream is a
        // code that is silently wrong.
        if (!parsed?.secret || !parsed.userId || !parsed.period_seconds) return null;
        return parsed;
      } catch {
        // Safari in private mode throws on access rather than returning null.
        return null;
      }
    },
    write(value) {
      try {
        localStorage.setItem(KEY, JSON.stringify(value));
      } catch {
        // Full, or blocked. The app still works: the secret is in memory for
        // this session and comes back from /me/session on the next launch.
      }
    },
    clear() {
      try {
        localStorage.removeItem(KEY);
      } catch {
        /* nothing to do */
      }
    },
  };
}

/** For the case the probe might argue for: keep it only in memory. */
export function memorySecretStore(): SecretStore {
  let held: StoredSecret | null = null;
  return {
    kind: "memory",
    read: () => held,
    write: (v) => {
      held = v;
    },
    clear: () => {
      held = null;
    },
  };
}

/* The session is separate from the secret on purpose: it has different
 * lifetime rules, and conflating them would mean a storage decision about the
 * secret silently signing people out. */
const SESSION_KEY = "damga.session.v1";

export function readSession<T>(): T | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function writeSession(value: unknown | null): void {
  try {
    if (value === null) localStorage.removeItem(SESSION_KEY);
    else localStorage.setItem(SESSION_KEY, JSON.stringify(value));
  } catch {
    /* see above */
  }
}

/* The till's own identity, and why it is not the session's.
 *
 * `counter_devices` existed from the first schema and nothing ever wrote to
 * it: the counter sent no device_id, so `touch_device` returned early every
 * time, the device lock could never fire, and the till-silence alert watched
 * an empty table and answered "all 0 tills reporting". This is the value that
 * gives all three a subject.
 *
 * SEPARATE FROM THE SESSION, DELIBERATELY. A shift ends and the staff member
 * signs out; the tablet is the same tablet. Storing this under the session key
 * would mean every sign-out abandoned the device row and every sign-in created
 * another, and a shop would accumulate a "Till 14" by Thursday. The session is
 * who is working; this is which counter they are standing at.
 *
 * A device that loses its storage registers again and the old row is retired
 * by the merchant. That is better than letting the client choose its own id,
 * which would let one till claim another's identity and its history. */
const DEVICE_KEY = "damga.device.v1";

export function readDeviceId(): string | null {
  try {
    return localStorage.getItem(DEVICE_KEY);
  } catch {
    /* Private browsing, or storage disabled. The counter still works: every
     * endpoint takes device_id as optional, and the merchant loses the
     * silence alert for this tablet rather than the ability to stamp. */
    return null;
  }
}

export function writeDeviceId(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(DEVICE_KEY);
    else localStorage.setItem(DEVICE_KEY, id);
  } catch {
    /* see above */
  }
}

/* Whether THIS DEVICE has a passkey for the signed-in customer.
 *
 * A DEVICE FACT, NOT AN ACCOUNT FACT, and that is the whole reason it lives
 * here rather than being read from the server. A credential belongs to the
 * authenticator that made it: the server knows a factor exists, and cannot
 * know whether the handset in someone's hand can use it. A customer with a
 * passkey on their old phone, holding a new one, should be offered a passkey —
 * and asking the server would tell us not to.
 *
 * Kept apart from the session for the same reason the device id is: signing
 * out ends a shift, it does not un-create a credential. */
const PASSKEY_KEY = "damga.passkey.v1";

export function readPasskeyEnrolled(): boolean {
  try {
    return localStorage.getItem(PASSKEY_KEY) === "1";
  } catch {
    // Storage unavailable. Reporting "no passkey" costs an offer the customer
    // may decline; reporting "yes" would hide the offer from somebody who
    // needs it. The cheap error is the right one.
    return false;
  }
}

export function writePasskeyEnrolled(enrolled: boolean): void {
  try {
    if (enrolled) localStorage.setItem(PASSKEY_KEY, "1");
    else localStorage.removeItem(PASSKEY_KEY);
  } catch {
    /* see above */
  }
}
